/**
 * AdminVendorsEnhanced - Enhanced Vendors list view with standardized table
 * 
 * Features:
 * - Uses AdminDataTable for consistent UI across order management
 * - Server-side sorting and filtering
 * - Column visibility and resizing
 * - Status toggle (active/inactive)
 */
import React, { useState, useCallback } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  MonoCell,
  StatusCell,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { vendorsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  Pencil,
  Trash2,
  Building2,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Plus,
  ToggleLeft,
  ToggleRight,
  Loader2,
} from 'lucide-react';

// ===================================================
// CONSTANTS
// ===================================================

const VENDOR_TYPES = [
  { value: 'awards', label: 'Awards' },
  { value: 'printing', label: 'Printing' },
  { value: 'large_format', label: 'Large Format' },
  { value: 'promo', label: 'Promo' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'services', label: 'Services' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_TERMS = [
  { value: 'NET_30', label: 'Net 30' },
  { value: 'NET_15', label: 'Net 15' },
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'PREPAID', label: 'Prepaid' },
];

const STATUS_OPTIONS = [
  { value: 'true', label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'false', label: 'Inactive', color: 'bg-gray-100 text-gray-500', icon: XCircle },
];

const TYPE_CONFIG = VENDOR_TYPES.reduce((acc, t) => {
  acc[t.value] = { value: t.value, label: t.label, color: 'bg-blue-100 text-blue-700' };
  return acc;
}, {});

// ===================================================
// COMPONENT
// ===================================================

const AdminVendorsEnhanced = ({ onViewVendor }) => {
  const { isAdmin } = useAuth();
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    vendor_type: 'other',
    email: '',
    phone: '',
    payment_terms: '',
    notes: ''
  });
  
  // Fetch function
  const fetchVendors = useCallback(async (params) => {
    const apiParams = {
      page: params.page,
      page_size: params.page_size,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
    };
    if (params.q) apiParams.q = params.q;
    if (params.vendor_type) apiParams.vendor_type = params.vendor_type;
    if (params.is_active !== undefined && params.is_active !== '') {
      apiParams.is_active = params.is_active === 'true';
    }
    return await vendorsAPI.getVendors(apiParams);
  }, []);
  
  // Use admin table hook
  const {
    data: vendors,
    total,
    loading,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchVendors,
    defaultSortBy: 'name',
    defaultSortDir: 'asc',
    defaultPageSize: 25,
    filterKeys: ['vendor_type', 'is_active'],
    transformResponse: (data) => ({
      items: data.vendors || data.items || [],
      total: data.total || 0
    })
  });
  
  // Form handlers
  const resetForm = () => {
    setFormData({
      name: '',
      vendor_type: 'other',
      email: '',
      phone: '',
      payment_terms: '',
      notes: ''
    });
  };
  
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    
    setSaving(true);
    try {
      await vendorsAPI.createVendor(formData);
      toast.success('Vendor created');
      setShowCreateModal(false);
      resetForm();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    
    setSaving(true);
    try {
      await vendorsAPI.updateVendor(editingVendor.id, formData);
      toast.success('Vendor updated');
      setShowEditModal(false);
      setEditingVendor(null);
      resetForm();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleActive = async (vendor) => {
    try {
      await vendorsAPI.updateVendor(vendor.id, { is_active: !vendor.is_active });
      toast.success(vendor.is_active ? 'Vendor deactivated' : 'Vendor activated');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update vendor');
    }
  };
  
  const openEditModal = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      vendor_type: vendor.vendor_type || 'other',
      email: vendor.email || '',
      phone: vendor.phone || '',
      payment_terms: vendor.payment_terms || '',
      notes: vendor.notes || ''
    });
    setShowEditModal(true);
  };
  
  // Bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      // Soft-delete vendors
      for (const id of selectedIds) {
        await vendorsAPI.deleteVendor(id, reason);
      }
      toast.success(`Deleted ${selectedIds.length} vendor(s)`);
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete vendors');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Vendor',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-gray-500" />
          </div>
          <span 
            className="font-semibold text-ivory-900 cursor-pointer hover:text-blue-600 truncate max-w-[200px]"
            onClick={() => onViewVendor?.(row.id)}
            title={value}
          >
            {value}
          </span>
        </div>
      )
    },
    {
      key: 'vendor_type',
      header: 'Type',
      sortable: true,
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700 capitalize">
          {value?.replace('_', ' ') || '—'}
        </span>
      )
    },
    {
      key: 'email',
      header: 'Contact',
      sortable: false,
      render: (value, row) => (
        <div className="space-y-0.5 text-sm">
          {value && (
            <div className="flex items-center gap-1 text-gray-600">
              <Mail className="w-3 h-3" />
              <span className="truncate max-w-[160px]">{value}</span>
            </div>
          )}
          {row.phone && (
            <div className="flex items-center gap-1 text-gray-500">
              <Phone className="w-3 h-3" />
              <span>{row.phone}</span>
            </div>
          )}
          {!value && !row.phone && <span className="text-gray-400">—</span>}
        </div>
      )
    },
    {
      key: 'payment_terms',
      header: 'Terms',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-600">
          {value ? PAYMENT_TERMS.find(t => t.value === value)?.label || value.replace('_', ' ') : '—'}
        </span>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center gap-1 text-sm ${value ? 'text-green-600' : 'text-gray-400'}`}>
          {value ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewVendor?.(row.id);
            }}
            title="View"
            data-testid={`view-vendor-${row.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(row);
            }}
            title="Edit"
            data-testid={`edit-vendor-${row.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(row);
            }}
            className={row.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
            title={row.is_active ? 'Deactivate' : 'Activate'}
            data-testid={`toggle-vendor-${row.id}`}
          >
            {row.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          </Button>
        </div>
      )
    }
  ];
  
  const filterLabels = {
    vendor_type: 'Type',
    is_active: 'Status',
  };
  
  return (
    <div className="space-y-6" data-testid="admin-vendors-enhanced">
      <AdminListHeader
        title="Vendors"
        subtitle={total > 0 ? `${total.toLocaleString()} vendors` : undefined}
        createLabel="Add Vendor"
        onCreate={() => {
          resetForm();
          setShowCreateModal(true);
        }}
        canCreate={isAdmin}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
      />
      
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search vendors..."
          className="flex-1 min-w-[200px]"
        />
        
        <StatusFilter
          value={filters.vendor_type || ''}
          onChange={(v) => setFilter('vendor_type', v)}
          options={VENDOR_TYPES}
          placeholder="All Types"
          label="Type"
        />
        
        <StatusFilter
          value={filters.is_active || ''}
          onChange={(v) => setFilter('is_active', v)}
          options={STATUS_OPTIONS}
          placeholder="All Status"
          label="Status"
        />
      </FilterBar>
      
      {hasActiveFilters && (
        <ActiveFilterChips
          filters={filters}
          filterLabels={filterLabels}
          onRemove={(key) => setFilter(key, '')}
          onClearAll={clearFilters}
          statusConfigs={{
            vendor_type: TYPE_CONFIG,
            is_active: {
              'true': { label: 'Active', color: 'bg-green-100 text-green-700' },
              'false': { label: 'Inactive', color: 'bg-gray-100 text-gray-500' }
            }
          }}
        />
      )}
      
      <AdminDataTable
        columns={columns}
        data={vendors}
        loading={loading}
        emptyMessage="No vendors found"
        emptyIcon={Building2}
        
        // Selection
        selectable={isAdmin}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        
        // Sorting
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        
        // Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        
        // Density
        density={density}
        onDensityChange={setDensity}
        
        // Column preferences
        showColumnSelector={true}
        resizableColumns={true}
        
        // Row click
        onRowClick={(row) => onViewVendor?.(row.id)}
        
        data-testid="vendors-data-table"
      />
      
      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          setEditingVendor(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? 'Edit Vendor' : 'Add Vendor'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter vendor name"
                data-testid="vendor-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_type">Type</Label>
                <Select
                  value={formData.vendor_type}
                  onValueChange={(v) => setFormData({ ...formData, vendor_type: v })}
                >
                  <SelectTrigger data-testid="vendor-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Select
                  value={formData.payment_terms || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, payment_terms: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="payment-terms-select">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="vendor@example.com"
                  data-testid="vendor-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid="vendor-phone-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg resize-none"
                rows={3}
                placeholder="Additional notes..."
                data-testid="vendor-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setEditingVendor(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={showEditModal ? handleUpdate : handleCreate}
              disabled={saving}
              data-testid="save-vendor-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                showEditModal ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="vendor"
        loading={bulkActionLoading}
        warnings={[
          'Vendors with associated POs or Bills cannot be deleted.',
          'Soft-deleted vendors can be restored by an administrator.'
        ]}
        requireReason={true}
      />
    </div>
  );
};

export default AdminVendorsEnhanced;
